import { useEffect } from 'react';
import { BrandLogo, ProductMark, PRODUCT_NAME } from '../components/Brand.jsx';
import ExternalLink from '../components/ExternalLink.jsx';
import { HeaderAccent } from '../components/TopBar.jsx';
import BrandFooter from '../components/BrandFooter.jsx';
import NavIcon from '../components/NavIcon.jsx';
import { BRAND } from '../lib/brand.js';
import { useScrollSpy } from '../lib/useScrollSpy.js';
import { PERMISSION_GROUPS, PERMISSION_JUSTIFICATIONS } from '../../../shared/permission-map.mjs';
import { PAGE_TITLE, PAGE_DESCRIPTION, SECTIONS, TOC, SUPPORT_EMAIL } from '../lib/security-content.js';

const DOC_TITLE = `${PAGE_TITLE} | ${PRODUCT_NAME}`;

// Public page (no sign-in): the title and description are set for search engines and restored on leaving.
function usePageMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = DOC_TITLE;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute('content');
    meta.setAttribute('content', PAGE_DESCRIPTION);
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else if (previousDescription !== null) meta.setAttribute('content', previousDescription);
    };
  }, []);
}

function PermissionTable() {
  return (
    <div className="space-y-4" data-testid="permission-table">
      {PERMISSION_GROUPS.map((g) => (
        <div key={g.id}>
          <h4 className="text-sm font-semibold text-slate-700">{g.title}</h4>
          <dl className="mt-1 divide-y divide-ipc-border rounded-md border border-ipc-border bg-white">
            {g.permissions.map((p) => (
              <div key={p} className="grid gap-1 px-3 py-2 sm:grid-cols-[16rem_1fr] sm:gap-4">
                <dt><code className="break-all text-xs text-ipc-primary">{p}</code></dt>
                <dd className="text-sm text-slate-700">{PERMISSION_JUSTIFICATIONS[p]}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

function Block({ block }) {
  if (typeof block === 'string') return <p className="text-base leading-7 text-slate-700">{block}</p>;
  if (block.permissions) return <PermissionTable />;
  return (
    <ul className="list-disc space-y-2 pl-5 text-base leading-7 text-slate-700">
      {block.list.map((li) => <li key={li}>{li}</li>)}
    </ul>
  );
}

function Toc() {
  const ids = TOC.map((t) => t.id);
  const [active, setActive] = useScrollSpy(ids, ids[0]);
  return (
    <nav aria-label="On this page" className="text-sm">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">On this page</p>
      <ul className="space-y-1">
        {TOC.map((t) => (
          <li key={t.id}>
            <a
              href={`#${t.id}`}
              onClick={() => setActive(t.id)}
              aria-current={active === t.id ? 'location' : undefined}
              className={`block rounded px-2 py-1 hover:bg-white hover:text-ipc-primary ${active === t.id ? 'bg-white font-semibold text-ipc-primary' : 'text-slate-700'}`}
            >
              {t.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** "/security": the public architecture and security FAQ. No session, no sidebar, no sign-in step. */
export default function Security() {
  usePageMeta();
  return (
    <div className="flex min-h-screen flex-col bg-ipc-surface">
      <header className="on-dark bg-ipc-primary text-white shadow-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
          <ExternalLink href={BRAND.siteUrl} className="shrink-0 rounded">
            <BrandLogo tone="light" className="h-6 w-auto sm:h-7" />
          </ExternalLink>
          <span aria-hidden="true" className="h-6 w-px shrink-0 bg-white/30" />
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold tracking-tight sm:text-base">
            <ProductMark tone="light" size={28} />
            <span className="truncate">{PRODUCT_NAME}</span>
          </span>
          <a href="/login" className="ml-auto rounded px-3 py-1.5 text-sm font-medium text-white underline underline-offset-4 hover:no-underline">Sign in</a>
        </div>
        <HeaderAccent />
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <h1 className="text-3xl font-semibold tracking-tight text-ipc-primary">{PAGE_TITLE}</h1>
          <p className="mt-3 max-w-3xl text-lg leading-7 text-slate-700">
            The questions security teams ask us most, answered plainly: where your data lives, what onGuard reads,
            how credentials are protected, how runs are monitored, who can sign in, and how long data is kept.
          </p>

          <div className="mt-8 grid gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
            <aside className="lg:sticky lg:top-6 lg:self-start">
              <Toc />
            </aside>
            <div className="space-y-6">
              {SECTIONS.map((s) => (
                <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} className="card scroll-mt-6">
                  <h2 id={`${s.id}-h`} className="flex items-center gap-3 text-xl font-semibold text-ipc-primary">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ipc-surface text-ipc-light">
                      <NavIcon id={s.icon} className="h-5 w-5" />
                    </span>
                    <a href={`#${s.id}`} className="hover:underline">{s.title}</a>
                  </h2>
                  <div className="mt-4 space-y-6">
                    {s.items.map((item) => (
                      <div key={item.q} className="space-y-3">
                        <h3 className="text-base font-semibold text-slate-900">{item.q}</h3>
                        {item.a.map((b, i) => <Block key={i} block={b} />)}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              <p className="text-sm text-slate-600">
                Questions? Write to{' '}
                <a className="text-ipc-light underline underline-offset-2 hover:no-underline" href={`mailto:${SUPPORT_EMAIL}?subject=Security%20question%20about%20onGuard`}>{SUPPORT_EMAIL}</a>.
              </p>
            </div>
          </div>
        </div>
      </main>
      <BrandFooter />
    </div>
  );
}
